import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { StackedGroupBarChart } from "@jana-technology/platform-ui";

import { RiskManagementNetMitigatedRiskChartDocument } from "$/__generated__/graphql";
import { useGlobalFiltersContext } from "contexts/filters";
import { NET_MITIGATED_DATA_KEYS_MAP } from "constants/charts";
import { xAccessor } from "helpers/charts";
import { useBaseBarChartProps } from "hooks/useBaseBarChartProps";

import { ChartCard } from "./ChartCard";
import { TOOLTIP_SEMIBOLD } from "constants/styles";

export const NetMitigatedRisk = () => {
  const {
    appliedWell,
    appliedRiskMitigationId,
    riskMitigationQueryResult,
    selectedWellName,
    appliedHierarchyName,
  } = useGlobalFiltersContext();

  const { riskMitigations } = riskMitigationQueryResult.data ?? {};

  const riskMitigationName =
    riskMitigations?.find(
      (mitigation) => mitigation?.id === appliedRiskMitigationId,
    )?.value ?? "";

  const [hierarchyId, wellName] = appliedWell;

  const netMitigatedChartResult = useQuery(
    RiskManagementNetMitigatedRiskChartDocument,
    {
      skip: !wellName.length,
      variables: {
        hierarchyId,
        wellName,
        riskMitigationTypeId: appliedRiskMitigationId,
      },
    },
  );

  const netMitigatedRiskStacks = useMemo(() => {
    const {
      postMitigationOperationalRisk,
      postMitigationWellEntryRisk,
      netMitigatedRisk,
    } = NET_MITIGATED_DATA_KEYS_MAP;

    const dataKeys = [
      postMitigationOperationalRisk,
      postMitigationWellEntryRisk,
      netMitigatedRisk,
    ];

    const tooltipTitle = selectedWellName?.label
      ? `${selectedWellName.label} (${appliedHierarchyName})`
      : "";

    if (
      netMitigatedChartResult.data?.riskManagementChartsForWellNode
        ?.netMitigatedRisk
    ) {
      const netMitigatedData =
        netMitigatedChartResult.data.riskManagementChartsForWellNode
          .netMitigatedRisk;

      return {
        dataKeys,
        data: [
          {
            title: tooltipTitle,
            x: "Baseline",
            [postMitigationOperationalRisk]: netMitigatedData.baselineRisk,
            [postMitigationWellEntryRisk]: 0,
            [netMitigatedRisk]: 0,
            tooltipExtras: [
              {
                label: "Mitigation",
                value: "Baseline",
                labelClassName: TOOLTIP_SEMIBOLD,
              },
              {
                label: "Well Lifetime",
                value: "30 Years",
              },
            ],
          },
          {
            title: tooltipTitle,
            x: "Post-Mitigation",
            [postMitigationOperationalRisk]:
              netMitigatedData.postMitigationOperationalRisk,
            [postMitigationWellEntryRisk]:
              netMitigatedData.postMitigationWellEntryRisk,
            [netMitigatedRisk]: netMitigatedData.netMitigatedRisk,
            tooltipExtras: [
              {
                label: "Mitigation",
                value: riskMitigationName,
                labelClassName: TOOLTIP_SEMIBOLD,
              },
              {
                label: "Well Lifetime",
                value: "30 Years",
              },
            ],
          },
        ],
      };
    }

    return {
      dataKeys,
      data: [],
    };
  }, [
    netMitigatedChartResult.data?.riskManagementChartsForWellNode
      ?.netMitigatedRisk,
    riskMitigationName,
    appliedHierarchyName,
    selectedWellName,
  ]);

  const baseBarChartProps = useBaseBarChartProps();

  return (
    <ChartCard
      loading={netMitigatedChartResult.loading}
      heading="Net Mitigated Risk"
    >
      <StackedGroupBarChart
        {...baseBarChartProps}
        {...netMitigatedRiskStacks}
        withBarLabels
        chartName="Net Mitigated Risk"
        xAccessor={xAccessor}
        xPadding={2.5}
        xAxisLabel={undefined}
      />
    </ChartCard>
  );
};

export default NetMitigatedRisk;
